import React, { useMemo } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { translate } from '@utils/i18n';
import FilterPanel from '@components/FilterPanel';
import SearchPanel from '@components/SearchPanel';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';
import useFilteredKitOrders from '../hooks/useFilteredKitOrders';
import useKitOrderFilters from '../hooks/useKitOrderFilters';
import { kitOrderActionTypes } from '../hooks/kitOrdersReducer';
import { INITIAL_KIT_ORDER_FILTERS, KIT_ORDERS_NAMESPACE } from '../constants';
import KitOrderStatusActionCell from './KitOrderStatusActionCell';
import KitOrderStatusCell from './KitOrderStatusCell';
const KIT_ORDER_COLUMNS = [
    { id: 'orderDate', field: 'orderDate', title: 'order_date', sortable: true },
    { id: 'purchaseOrder', field: 'purchaseOrder', title: 'purchase_order_number', sortable: true },
    {
        id: 'standardKitsCount',
        field: 'standardKitsCount',
        title: 'number_of_standard_kits',
        sortable: true,
        type: 'number'
    },
    {
        id: 'smallDryKitsCount',
        field: 'smallDryKitsCount',
        title: 'number_of_small_dry_kits',
        sortable: true,
        type: 'number'
    },
    {
        id: 'largeDryKitsCount',
        field: 'largeDryKitsCount',
        title: 'number_of_large_dry_kits',
        sortable: true,
        type: 'number'
    },
    { id: 'orderSentBy', field: 'orderSentBy', title: 'order_sent_by', sortable: true },
    { id: 'status', field: 'status', title: 'status', sortable: true, cell: KitOrderStatusCell },
    { id: 'actions', field: 'actions', title: 'actions', sortable: false, cell: KitOrderStatusActionCell }
];
export default function KitOrderTable() {
    const { data, totalCount, isLoading, error, page, sort, filters, dispatch, onResetCache } = useFilteredKitOrders();
    const { filtersList, onSearchChange } = useKitOrderFilters({ filters, dispatch });
    const onResetFilters = () => {
        dispatch({ type: kitOrderActionTypes.FILTER_CHANGED, filters: INITIAL_KIT_ORDER_FILTERS });
    };
    const visibleColumns = useMemo(() => KIT_ORDER_COLUMNS.map((c) => (Object.assign(Object.assign({}, c), { title: translate(`kit_orders.columns.${c.title}`, {
            namespace: KIT_ORDERS_NAMESPACE,
            defaultValue: c.title
        }) }))), []);
    return (React.createElement("div", { className: "kit-orders-container" },
        React.createElement(FilterPanel, { onResetFilters: onResetFilters, onResetCache: onResetCache, filters: filtersList },
            React.createElement("div", { className: "filters__group--search" },
                React.createElement(SearchPanel, { placeholder: translate('search', { namespace: 'filters' }), onChange: onSearchChange }))),
        error && (React.createElement("div", { className: "workspace__section" },
            React.createElement("div", { className: "qmb-alert--error" },
                React.createElement("i", { className: "fa-light fa-exclamation-triangle alert__visual" }),
                error.message || translate('error', { namespace: 'generic' })))),
        isLoading && (React.createElement("div", { className: "workspace__section" },
            React.createElement("div", { className: "qmb-loading--96", "data-title": translate('loading', { namespace: 'generic' }) },
                React.createElement("svg", { role: "img" },
                    React.createElement("use", { href: "/map.svg#load-spinner" }))))),
        !isLoading && !error && data && (React.createElement(CustomScrollbar, null,
            React.createElement(Grid, { className: "qmb-grid--01", data: data, dataItemKey: "id", total: totalCount, skip: page.skip, take: page.take, onPageChange: (event) => {
                    dispatch({ type: kitOrderActionTypes.PAGE_CHANGED, page: event.page });
                }, pageable: {
                    type: 'input',
                    pageSizes: [10, 25, 50],
                    pageSizeValue: page.take
                }, pager: EnterKeyPager, sortable: true, sort: sort, onSortChange: (event) => {
                    dispatch({ type: kitOrderActionTypes.SORT_CHANGED, sort: event.sort });
                } }, visibleColumns.map((column) => (React.createElement(Column, { key: column.id, field: column.field, title: column.title, cell: column.cell, sortable: column.sortable }))))))));
}
